/*
 * useAvailabilityView — memoised derivations for the Availability page.
 *
 * Given raw `rows` (per-day availability docs) + the current filters,
 * returns everything the table needs to render:
 *   listedGuards   — guards that appear in the current row set
 *   filtered       — rows after guard + kind filters
 *   groupedRows    — consecutive same-key days collapsed into range rows
 *
 * Sorting is left to the caller (`useSortableRows` in the parent) since
 * that hook manages persisted UI state (default key/dir + storageKey).
 */
import { useMemo } from "react";
import { groupKey, nextDay } from "./lib";

export function useAvailabilityView({ rows, guards, guardFilter, kindFilter }) {
  const listedGuards = useMemo(() => {
    const ids = new Set(rows.map((r) => r.guard_id).filter(Boolean));
    return guards.filter((g) => ids.has(g.id));
  }, [rows, guards]);

  const filtered = useMemo(() => {
    let list = rows;
    if (guardFilter.length) {
      const s = new Set(guardFilter);
      list = list.filter((r) => s.has(r.guard_id));
    }
    if (kindFilter.length) {
      const s = new Set(kindFilter);
      // Released entries are displayed as "available" per product spec.
      list = list.filter((r) => {
        const effective = r.kind === "released" ? "available" : r.kind;
        return s.has(effective);
      });
    }
    return list;
  }, [rows, guardFilter, kindFilter]);

  // Group consecutive per-day docs into a single "range" row when they share
  // guard + kind + reasons + time window + notes AND their dates are adjacent
  // (no gap). Preserves the underlying doc ids so edit/delete can fan out.
  // Released rows are grouped separately (each still one row per released assignment).
  const groupedRows = useMemo(() => {
    if (!filtered.length) return [];
    const sorted = [...filtered].sort((a, b) => {
      const ka = groupKey(a);
      const kb = groupKey(b);
      if (ka !== kb) return ka.localeCompare(kb);
      return (a.date || "").localeCompare(b.date || "");
    });
    const groups = [];
    for (const r of sorted) {
      const k = groupKey(r);
      const g = groups[groups.length - 1];
      if (g && g._key === k && nextDay(g.end_date) === r.date) {
        g.end_date = r.date;
        g.ids.push(r.id);
        g.count += 1;
      } else {
        groups.push({
          _key: k,
          _sample: r,
          ids: [r.id],
          count: 1,
          guard_id: r.guard_id,
          guard_name: r.guard_name,
          kind: r.kind,
          reasons: r.reasons || [],
          start_time: r.start_time,
          end_time: r.end_time,
          notes: r.notes,
          source: r.source,
          start_date: r.date,
          end_date: r.date,
          released_from_site_name: r.released_from_site_name,
        });
      }
    }
    return groups;
  }, [filtered]);

  return { listedGuards, filtered, groupedRows };
}
